import React, { useEffect, useState } from 'react';
import { collection, addDoc, query, where, getDocs, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useParams } from 'react-router-dom'; 
import '../../Styles/Documentacion.css';
import GetUserData from '../../Hooks/GetUserData';
import { useUserPermissions } from '../../Hooks/GetUserPermissions';
import { getFunctions, httpsCallable } from "firebase/functions";
import { GetHubInfo } from '../../Hooks/UseHubName';


const FolderTree = ({ onSelectFolder, selectedUserId, onSelectFolderObject, refresh }) => {
  const [folders, setFolders] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [currentParentId, setCurrentParentId] = useState(null);
  const [currentParentFolder, setCurrentParentFolder] = useState(null);
  const [openFolders, setOpenFolders] = useState(new Set());
  const { hubId } = useParams();
  const hubInfo = GetHubInfo(hubId);
  const {userData} = GetUserData();

  const { permisos } = useUserPermissions();
  const functions = getFunctions();

  useEffect(() => {
    if (!hubId) return;
    fetchFolders();
    //eslint-disable-next-line
  }, [hubId, refresh]); // <-- agrega refresh como dependencia


  const fetchFolders = async () => {
    try {
      const getUserFolders = httpsCallable(functions, 'getUserFolders');
      const result = await getUserFolders({ hubId });
      const foldersData = result.data.folders || [];
      setFolders(foldersData);
    } catch (error) {
      console.error('Error al obtener carpetas desde función:', error);
      setFolders([]); // Opcional: limpiar si falla
    }
  };


  const toggleFolder = (folderId) => {
    setOpenFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const renderFolders = (parentId) => {
    return folders
      .filter((folder) => folder.parentId === parentId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((folder) => {
        const hasChildren = folders.some((f) => f.parentId === folder.id);
        const isOpen = openFolders.has(folder.id);

        // Mostrar solo el permiso del selectedUserId, si existe
        const userPermission = selectedUserId? folder.permisosUsuarios?.[selectedUserId] : '';

        return (
          <li key={folder.id} className="ml-4 mb-2">
            <div className="flex items-center space-x-2">
              {hasChildren && (
                <button
                  onClick={() => toggleFolder(folder.id)}
                  className="folder-toggle-btn"
                  aria-label={isOpen ? 'Cerrar carpeta' : 'Abrir carpeta'}
                >
                  {isOpen ? '📂' : '📁'}
                </button>
              )}
              <span
                onClick={() => {
                  onSelectFolder(folder.name, folder.id);
                  setCurrentParentId(folder.id);
                  setCurrentParentFolder(folder);
                  if (onSelectFolderObject) {
                    onSelectFolderObject(folder); // << nuevo
                  }
                  
                }}
                className="folder-item cursor-pointer hover:underline"
              >
                {folder.name}
                {userPermission && (
                  <span style={{ marginLeft: '8px', fontSize: '0.8em', color: '#888' }}>
                    ({userPermission})
                  </span>
                )}
              </span>
            </div>
            {isOpen && <ul>{renderFolders(folder.id)}</ul>}
          </li>
        );
      });
  };


  const parentFolderName = currentParentId
    ? folders.find((folder) => folder.id === currentParentId)?.name || ''
    : '';

  const handleCreateFolder = async (e) => {
    e.preventDefault();


    if(!tienePermisosEscritura()) {
      alert("Permisos insuficientes para crear una carpeta");
      return;
    }

    if (!newFolderName.trim()) return;

    try {
      await addDoc(collection(db, 'private-hubs', hubId, 'Carpetas'), {
        name: newFolderName.trim(),
        parentId: currentParentId,
        isPublic: true,
      });
      setNewFolderName('');
    } catch (error) {
      console.error('Error creando carpeta:', error);
    }
    finally {
      fetchFolders(); // Refrescar lista de carpetas
    }
  };

  const tienePermisosEscritura = () => {
    const permisosGenerales = permisos.verDocs;
    if(!currentParentFolder) {
      return permisosGenerales === 'lectura_escritura';
    }
    const permisosCarpeta = currentParentFolder.permiso || '';

    if(permisosCarpeta === 'lectura_escritura'){
      return true;
    }
    if(permisosGenerales === 'lectura_escritura' && (permisosCarpeta !== 'lectura')) {
      return true;
    }
    return false;
  }

  const deleteFolderIfEmpty = async (folderId, hubId, userData) => {
    if(!tienePermisosEscritura()) {
      alert("Permisos insuficientes para eliminar esta carpeta");
      return;
    }

    if(!hubInfo.useBlocktrace){

      if(!window.confirm('¿Quieres eliminar esta carpeta? Esta acción no se puede deshacer.')) return;

      try {
        // 1) Consultar carpetas hijas
        const foldersQuery = query(
          collection(db, 'private-hubs', hubId, 'Carpetas'), 
          where('parentId', '==', folderId)
        );
        const foldersSnapshot = await getDocs(foldersQuery);
        if (!foldersSnapshot.empty) {
          alert('No se puede eliminar la carpeta porque tiene subcarpetas.');
          return;
        }

        // 2) Consultar documentos en esa carpeta
        const docsQuery = query(
          collection(db, 'private-hubs', hubId, 'Documentos'),
          where('folderId', '==', folderId)
        );
        const docsSnapshot = await getDocs(docsQuery);
        if (!docsSnapshot.empty) {
          alert('No se puede eliminar la carpeta porque tiene documentos.');
          return;
        }

        // 3) Eliminar carpeta
        await deleteDoc(doc(db, 'private-hubs', hubId, 'Carpetas', folderId));
        alert('Carpeta eliminada con éxito.');
        fetchFolders(); // Refrescar lista de carpetas

      } catch (error) {
        console.error('Error eliminando carpeta:', error);
        alert('Error al eliminar la carpeta.');
      }
    }
    else{
      if(!window.confirm('¿Quieres ocultar esta carpeta a todos los usuarios del hub? Esta acción no se puede deshacer.')) return;

      try {
        const q = doc(db, 'private-hubs', hubId, 'Carpetas', folderId);
        const folderDoc = await getDoc(q);

        const docsQuery = query(
          collection(db, 'private-hubs', hubId, 'Documentos'),
          where('folderId', '==', folderId)
        );
        const docsSnapshot = await getDocs(docsQuery);
        if (docsSnapshot.empty) {
          await deleteDoc(doc(db, 'private-hubs', hubId, 'Carpetas', folderId));
          alert('Carpeta eliminada con éxito.'); 
          fetchFolders(); // Refrescar lista de carpetas
          return;
        }
        const updated = await updateDocumentVisibility(docsSnapshot.docs[0].data().blocktraceInfo.documentId, userData.email, false); 
        if(updated.error){
          alert("Error al actualizar visibilidad: " + updated.error);
          return;
        }
        const removed = await removeAccessFromDocuments(docsSnapshot.docs.map(doc => doc.data().blocktraceInfo.documentId), userData.email);
        if(removed.error){
          alert("Error error al retirar acceso: " + removed.error);
          return;
        }
        updateFolderPermissions(folderId, hubId, userData.uid);
          } 
      catch (error) {
        console.error("Error :", error);
        return { success: false, error: error.message };
      }
    } 
  };

  async function getDocumentMetadata(documentId, userId) {
    try {
      console.log("Consultando metadata para documentId:", documentId, "userId:", userId);
      const url = `https://us-central1-lyseis-tools-hub.cloudfunctions.net/blocktraceGetMetadata?documentId=${encodeURIComponent(documentId)}&userId=${encodeURIComponent(userId)}`;
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Accept": "application/json"
        }
      });

      if (response.status === 403) {
        throw new Error("No tienes permiso para acceder a este documento (403).");
      }

      if (response.status === 404) {
        throw new Error("El documento no existe (404).");
      }

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Error al consultar metadata: ${response.status} - ${errText}`);
      }

      const data = await response.json();
      console.log("Metadata del documento:", data);

      return data; // contiene { success, documentId, isPublic, metadata }

    } catch (error) {
      console.error("Error en getDocumentMetadata:", error);
      return { success: false, error: error.message };
    }
  }

  async function updateDocumentVisibility(documentId, userId, isPublic) {
    const url = `https://us-central1-lyseis-tools-hub.cloudfunctions.net/blocktraceUpdateVisibility?documentId=${encodeURIComponent(documentId)}&userId=${encodeURIComponent(userId)}`;
    
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ isPublic})
    });

    const data = await response.json();
    console.log("Respuesta:", data);
    return data;
  }

  async function updateFolderPermissions(folderId, hubId, userId) {
    try {
      const response = await fetch("https://us-central1-lyseis-tools-hub.cloudfunctions.net/updateFolderPermissions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          folderId,
          hubId,
          userId
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error("Error al actualizar permisos:", data.error);
        return null;
      }

      console.log("Permisos actualizados correctamente:", data);
      return data;

    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  }

  async function removeAccessFromDocuments(documentIds, userId) {
    const FUNCTION_URL = "https://blocktraceremoveaccess-eosrmhmqna-uc.a.run.app";

    try {
      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          documentIds,
          userId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error al eliminar accesos:", errorText);
        throw new Error("No se pudo completar la operación");
      }

      const data = await response.json();
      console.log("Accesos eliminados correctamente:", data);
      return data;

    } catch (error) {
      console.error("Error en removeAccessFromDocuments:", error);
      throw error;
    }
  }



  return (
    <div>
      <form onSubmit={handleCreateFolder} className="folder-form">
        <input
          type="text"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          placeholder={
            currentParentId
              ? `Nueva subcarpeta en "${parentFolderName}"`
              : 'Nueva carpeta raíz'
          }
          className="border border-gray-300 rounded px-3 py-1 w-full"
        />
        <button
          type="submit"
          className="mt-2 bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700 w-full"
        >
          Crear carpeta
        </button>
      </form>



      <ul className="text-sm text-gray-700">{renderFolders(null)}</ul>
      {/* Botón para borrar carpeta seleccionada */}
      <div className='form-new-folder'>
        <button
          disabled={!currentParentId}
          onClick={() => deleteFolderIfEmpty(currentParentId, hubId, userData)}
        >
          Eliminar carpeta seleccionada
        </button>
      </div>
    </div>
  );
};

export default FolderTree;
